import dayjs from 'dayjs';
import { NO_BG_USAGE_KEY } from './constants';

// เหมือน logic ฝั่ง server (project.service.js patchDashboard) เป๊ะ — string compare 'YYYY-MM-DD'
// ใช้แค่คำนวณ optimistic patch ก่อน server ตอบกลับ ค่าจริงยึดตาม legal_warranty ที่ server derive ให้เสมอ
export function deriveLegalWarranty(dateStr) {
  if (!dateStr) return false;
  return String(dateStr).slice(0, 10) >= dayjs().format('YYYY-MM-DD');
}

export function computeHasCommitment(p) {
  return (
    p?.work_status !== 'cancelled' && // ยกเลิก = ไม่มีภาระผูกพันอีกต่อไป
    (p?.work_status !== 'done' ||
      p?.finance_status !== 'completed_po' ||
      p?.legal_warranty ||
      p?.legal_debt ||
      p?.legal_lawsuit ||
      p?.report_status === 'pending') // ต้องส่งรายงานแต่ยังไม่ได้ส่ง = ยังมีภาระผูกพัน
  );
}

// คำนวณ patch พร้อม cascade เดียวกับ handleDim3Change ใน ProjectDashboardTable.js (P5) เป๊ะ
// เพื่อให้พฤติกรรมเหมือนกันทุกจุดที่แก้ไขสถานะได้ (P5 ↔ P111 dashboard)
export function computeStatusCascade(current, field, value) {
  const patch = { [field]: value };

  if (
    field === 'finance_status' &&
    (value === 'completed_po' || value === 'last_installment')
  ) {
    patch.work_status = 'done';
  }
  if (
    field === 'work_status' &&
    (value === 'waiting' || value === 'in_progress')
  ) {
    patch.legal_warranty = false;
    patch.warranty_end_date = null;
    patch.bg_usage_category = '';
  }
  if (field === 'warranty_end_date') {
    patch.legal_warranty = deriveLegalWarranty(value);
    if (value) patch.work_status = 'done';
  }

  const effectiveWs =
    patch.work_status !== undefined ? patch.work_status : current?.work_status;

  if (field === 'bg_usage_category' && value === NO_BG_USAGE_KEY && effectiveWs !== 'done') {
    return null; // บล็อก — งานยังไม่เสร็จ 100% เลือกไม่ได้ (เหมือน P5)
  }

  const currentProgress = Number(current?.progress) || 0;
  if (effectiveWs === 'done' && currentProgress < 100) {
    patch.progress = 100;
  } else if (effectiveWs !== 'done' && currentProgress >= 100) {
    patch.progress = 0;
  }
  return patch;
}

// sync ต่อเนื่องทุกครั้งที่ row2(วางบิล)/row3(รับเงิน) เปลี่ยน — ไม่ใช่แค่ unlock ครั้งเดียวจาก 'none'
// ไต่ลำดับ none → partial/billed_waiting → last_installment ตามตัวเลขจริงเทียบกับ po (PO No VAT):
//   ยังไม่มีเงินเข้าเลย                                → none
//   บิล/รับเงินมาแล้ว แต่ยังบิลไม่ครบ PO (เหลือ "ยอดค้าง")   → partial (มีรับเงินแล้ว) / billed_waiting (ยังไม่รับเลย)
//   บิลครบ PO แล้ว (ยอดค้าง=0) แต่ยังรับเงินไม่ครบ 100%      → last_installment (รอรับงวดสุดท้าย)
// "แข็ง" ที่ completed_po เสมอ — ไม่เดาให้แม้จ่ายครบ 100% แล้ว ต้องให้ user กดยืนยันเอง (ปลอดภัยกว่า)
// last_installment บังคับ work_status='done' ไปด้วย เหมือนตอน user เลือกเองผ่าน computeStatusCascade
export function computeFinanceSync(current, po, billedCombined, receivedCombined) {
  const status = current?.finance_status || '';
  if (status === 'completed_po') return null; // จบแล้ว ไม่แตะ

  const poVal = Number(po) || 0;
  const remaining = poVal - billedCombined - receivedCombined;
  let target;
  if (billedCombined <= 0 && receivedCombined <= 0) {
    target = 'none';
  } else if (remaining > 0) {
    target = receivedCombined > 0 ? 'partial' : 'billed_waiting';
  } else if (receivedCombined >= poVal) {
    return null; // ครบ 100% แล้ว — รอ user กดยืนยัน completed_po เอง
  } else {
    target = 'last_installment';
  }

  if (target === status) return null;
  const patch = { finance_status: target };
  if (target === 'last_installment') patch.work_status = 'done';
  return patch;
}

// มีเงินวางบิล/รับเงินเข้ามาแล้ว แต่ work_status ยังค้างที่ "รอเข้าดำเนินการ" (หรือค่า default "")
// ให้ขยับเป็น "ดำเนินการ (ทำเอง)" อัตโนมัติ — เดา work_method ไม่ได้ว่าจ้างเหมา/ผสมหรือเปล่า
// เลือก "ทำเอง" เป็น default ที่ user ยืนยันไว้ (แก้เป็นวิธีอื่นเองได้ทีหลังถ้าไม่ตรง)
export function computeWorkAutoBump(current, billedCombined, receivedCombined) {
  const status = current?.work_status || '';
  if (status !== '' && status !== 'waiting') return null;
  if (billedCombined > 0 || receivedCombined > 0) {
    return { work_status: 'in_progress', work_method: 'self' };
  }
  return null;
}

// ตรงข้ามกับ computeWorkAutoBump — คืนเฉพาะตอน work_status/work_method ยังตรงกับค่าที่ auto-bump
// เคยตั้งไว้เป๊ะ (in_progress + self) เท่านั้น ถ้า user เปลี่ยนเป็นจ้างเหมา/ผสม หรือทำเสร็จแล้วเอง จะไม่แตะ
export function computeWorkAutoRevert(current, billedCombined, receivedCombined) {
  if (billedCombined > 0 || receivedCombined > 0) return null;
  if (current?.work_status === 'in_progress' && current?.work_method === 'self') {
    return { work_status: 'waiting', work_method: '' };
  }
  return null;
}

export const fmt = (n) => {
  const num = n == null ? 0 : Number(n);
  if (num === 0) return '-';
  return num.toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const fmtPct = (num, den) => {
  return den ? `${((num / den) * 100).toFixed(2)}%` : null;
};

export const fmtDate = (d) => {
  if (!d) return null;
  try {
    return dayjs(d).format('DD/MM/YYYY');
  } catch {
    return null;
  }
};

export const toDateInput = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toISOString().split('T')[0];
  } catch {
    return '';
  }
};

// เส้นใต้ตัวเลข — เส้นเดียว = ยอดก่อนรวม, เส้นคู่ = ยอดหลังรวม (ผลลัพธ์/subtotal) แบบบัญชี
export const underlineSx = (underline) => {
  if (underline === 'double') {
    return { borderBottom: '3px double currentColor', pb: '1px' };
  }
  if (underline === 'single') {
    return { borderBottom: '1px solid currentColor', pb: '1px' };
  }
  return {};
};

export const underlineWidthSx = (underline) => {
  if (underline === 'double' || underline === 'single') {
    return {
      display: 'inline-block',
      width: '80px',
      textAlign: 'right',
    };
  }
  return {};
};

// คืนความกว้างเป็น px เสมอ (ไม่ใช้หน่วย ch) — ch คำนวณตาม font-size ของกล่องเอง
// ซึ่งมักไม่ตรงกับ font-size เล็กของตัวเลขข้างใน ทำให้ความกว้างบวมเกินสัดส่วนจริง
export const getAutoColWidth = (text, minCh, maxCh, padCh = 2, pxPerCh = 7.5) => {
  const raw = text == null ? '' : String(text);
  const len = raw.length || 1;
  const ch = Math.max(minCh, Math.min(maxCh, len + padCh));
  return `${Math.round(ch * pxPerCh)}px`;
};

export const getExpandedWidthFromDefault = (text, defaultPx, defaultCh, maxCh, padCh = 2) => {
  const raw = text == null ? '' : String(text);
  const neededCh = (raw.length || 1) + padCh;
  if (neededCh <= defaultCh) return defaultPx;
  // อนุมาน px-ต่อ-ch จากอัตราส่วน defaultPx/defaultCh เดิม ให้ต่อเนื่องกับขนาด default
  const pxPerCh = parseFloat(defaultPx) / defaultCh;
  return getAutoColWidth(raw, defaultCh, maxCh, padCh, pxPerCh);
};

export const getLongestText = (items) =>
  items.reduce((longest, item) => {
    const next = item == null ? '' : String(item);
    return next.length > longest.length ? next : longest;
  }, '');
